import { GraphQLSchema } from 'graphql';
import React from 'react';

import {
  OperationPath,
  OperationPathSegment,
  operationPathToGraphQLPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { assertUnreachable } from 'src/lib/assertUnreachable';

import { useAnalyticsContext } from '../useAnalyticsContext';

type StepUp = { type: 'step up' };
type StepDownTo = { type: 'step down to'; node: OperationPathSegment };
type GoUpTo = { type: 'go up to'; index: number };
type NavigateTo = { type: 'navigate to'; path: OperationPath };
type Load = { type: 'load'; selectedTab: string };
export type DocsNavigationAction =
  | StepDownTo
  | StepUp
  | GoUpTo
  | NavigateTo
  | Load;
type DocsNavState = {
  path: OperationPath;
  paths: Record<string, OperationPath>;
  currentTab: string;
};

function reducePath(
  { path, paths, currentTab }: DocsNavState,
  action: DocsNavigationAction,
): DocsNavState {
  switch (action.type) {
    case 'step up': {
      return { path: path.slice(0, -1), paths, currentTab };
    }
    case 'go up to': {
      return { path: path.slice(0, action.index + 1), paths, currentTab };
    }
    case 'step down to': {
      return { path: path.concat(action.node), paths, currentTab };
    }
    case 'navigate to': {
      return { path: action.path, paths, currentTab };
    }
    case 'load': {
      const newPaths = {
        ...paths,
        [currentTab]: path,
      };
      return {
        path: paths[action.selectedTab] ?? path,
        paths: newPaths,
        currentTab: action.selectedTab,
      };
    }
    default:
      return assertUnreachable(action);
  }
}

export const useDocsPath = (
  schema: GraphQLSchema,
  selectedTab: string,
): [OperationPath, React.Dispatch<DocsNavigationAction>] => {
  const { track } = useAnalyticsContext();

  const navStateReducer = React.useCallback(
    (
      { path, paths, currentTab }: DocsNavState,
      action: DocsNavigationAction,
    ): DocsNavState => {
      const {
        path: newPath,
        paths: newPaths,
        currentTab: newTab,
      } = reducePath({ path, paths, currentTab }, action);
      if (operationPathToGraphQLPath(newPath, schema)) {
        track(`Navigate Docs`, { label: action.type });
        return { path: newPath, paths: newPaths, currentTab: newTab };
      }
      return { path, paths, currentTab };
    },
    [track, schema],
  );

  const queryType: OperationPathSegment = { kind: 'Query' };

  const [doc, dispatchNavAction] = React.useReducer(navStateReducer, {
    path: queryType ? [queryType] : [],
    paths: {},
    currentTab: selectedTab,
  });

  React.useEffect(() => {
    dispatchNavAction({ type: 'load', selectedTab });
  }, [selectedTab, dispatchNavAction]);

  return [doc.path, dispatchNavAction];
};
